import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero from '@/components/shared/PageHero';
import { INDEXED_SALARY_OCCUPATIONS, INDEXED_TRENDS_OCCUPATIONS } from '@/lib/seoIndexing';
import { HIGHEST_PAYING_HUBS } from '@/lib/highestPayingHubs';

function titleFromSlug(slug: string) {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * A–Z browse index of curated occupations so every salary page is within a
 * few clicks of the homepage (SMART W5–6).
 */
const OccupationsIndex = () => {
  const year = new Date().getFullYear();
  const occupations = [...INDEXED_SALARY_OCCUPATIONS].sort();
  const trendsSet = new Set(INDEXED_TRENDS_OCCUPATIONS);

  const byLetter = occupations.reduce<Record<string, string[]>>((acc, slug) => {
    const letter = slug.charAt(0).toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(slug);
    return acc;
  }, {});

  const letters = Object.keys(byLetter).sort();

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={`All Occupations A–Z (${year}) | Wage Atlas`}
        description={`Browse ${occupations.length} curated occupations with official BLS OEWS salary data by state and nationally.`}
        canonical="https://wageatlas.com/occupations/"
      />

      <PageHero
        badge={{ icon: Briefcase, label: 'Occupation Index' }}
        title="All Occupations"
        subtitle={
          <>
            {occupations.length} curated careers with national and state salary pages from BLS OEWS.
            Start here to browse without relying on search alone.
          </>
        }
      />

      <div className="container mx-auto px-4 py-8 space-y-8">
        <div className="flex flex-wrap gap-2">
          {letters.map((letter) => (
            <a
              key={letter}
              href={`#letter-${letter}`}
              className="px-3 py-1 rounded border text-sm hover:bg-muted"
            >
              {letter}
            </a>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Comparison hubs</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3 text-sm">
            {HIGHEST_PAYING_HUBS.map((hub) => (
              <Link
                key={hub.slug}
                to={hub.existingRoute || `/highest-paying/${hub.slug}/`}
                className="text-primary hover:underline"
              >
                {hub.title}
              </Link>
            ))}
          </CardContent>
        </Card>

        {letters.map((letter) => (
          <section key={letter} id={`letter-${letter}`} className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-4 border-b pb-2">{letter}</h2>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byLetter[letter].map((slug) => (
                <li key={slug} className="text-sm">
                  <Link
                    to={`/salary/${slug}/united-states/`}
                    className="font-medium text-primary hover:underline"
                  >
                    {titleFromSlug(slug)}
                  </Link>
                  <div className="text-xs text-muted-foreground mt-1 space-x-2">
                    <Link to={`/best-states/${slug}/`} className="hover:underline">
                      Best states
                    </Link>
                    {trendsSet.has(slug) && (
                      <Link
                        to={`/trends/${slug}/united-states/`}
                        className="hover:underline"
                      >
                        Trends
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

export default OccupationsIndex;
